import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Weeks run Monday through Sunday (ISO 8601), matching week-slider.js and
// getAvailability.js elsewhere in this codebase — dayjs's plain
// startOf('week') is locale-dependent and defaults to Sunday.
dayjs.extend(isoWeek);
// A restaurant's Monday is its own Monday. Without these the window was cut
// at the BROWSER's midnight, so the same report answered differently
// depending on where the owner happened to be reading it from — and an order
// taken near closing time fell into the neighbouring week.
dayjs.extend(utc);
dayjs.extend(timezone);

// "Now", in the store's day rather than the reader's. An unknown or unset
// zone falls back to the browser, which is what this always did.
export const zonedNow = (zone) => {
  if (!zone) return dayjs();
  try {
    return dayjs().tz(zone);
  } catch {
    // An id the browser's Intl database does not know. Guessing a different
    // zone would be worse than reading the local clock, which is at least
    // the behaviour every screen had before.
    return dayjs();
  }
};

// A date the reader picked on a calendar means that calendar date at the
// store, not the same instant re-read somewhere else — so the day is carried
// across as a day, not as a timestamp.
export const zonedDay = (value, zone) => {
  if (!value || !zone) return value || null;
  try {
    return dayjs.tz(value.format('YYYY-MM-DD'), zone);
  } catch {
    return value;
  }
};

export const zonedCustom = (custom, zone) => ({
  from: zonedDay(custom?.from || null, zone),
  to: zonedDay(custom?.to || null, zone),
});

// Preset ids are plain ASCII on purpose. The report-store presets carry
// invisible zero-width characters in their ids; nothing here reuses them.
export const PERIOD_PRESETS = [
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom' },
];

// The statement screen is weekly (spec 2026-09-14 §5), and it is the page a
// restaurant opens most, so the whole section defaults to the same window.
export const DEFAULT_PRESET = 'this_week';

const EMPTY = { from: null, to: null };

// Resolves a preset to calendar days. `now` decides whose days those are:
// pass `zonedNow(storeZone)` and every startOf/endOf below lands on the
// store's midnight instead of the browser's.  `custom` is the picker's own
// {from, to} (dayjs or null on either side) and is only read for 'custom'.
export const resolvePeriod = (preset, custom, now = dayjs()) => {
  switch (preset) {
    case 'this_week':
      return { from: now.startOf('isoWeek'), to: now.endOf('isoWeek') };
    case 'last_week': {
      const lastWeek = now.subtract(1, 'week');
      return { from: lastWeek.startOf('isoWeek'), to: lastWeek.endOf('isoWeek') };
    }
    case 'last_month': {
      const lastMonth = now.subtract(1, 'month');
      return { from: lastMonth.startOf('month'), to: lastMonth.endOf('month') };
    }
    case 'last_30_days':
      return { from: now.subtract(29, 'day'), to: now };
    case 'custom':
      return { from: custom?.from || null, to: custom?.to || null };
    case 'this_month':
      return { from: now.startOf('month'), to: now };
    default:
      return { from: now.startOf('isoWeek'), to: now.endOf('isoWeek') };
  }
};

// The API compares `createdate >= from && createdate <= to` on UTC
// timestamps, so the day boundaries become instants here and the end bound is
// the day's last millisecond. Whose day they are was already decided by the
// dayjs objects handed in — this only converts.
export const toPeriodQuery = ({ from, to } = EMPTY) => ({
  from: from ? from.startOf('day').toISOString() : null,
  to: to ? to.endOf('day').toISOString() : null,
});

// A window counts as a full calendar month if its bounds are exactly that
// month's start and end — used below so a custom range left over from a
// previous monthly step keeps stepping by month instead of falling back to
// a day-count shift the moment the preset label becomes 'custom'.
const isFullCalendarMonth = (from, to) =>
  from.isSame(from.startOf('month'), 'day') && to.isSame(from.endOf('month'), 'day');

// Shifts the window currently on screen by its own length. The result is
// always `custom`: "this week, one step back" is not this week any more, and
// leaving the preset highlighted would make the control describe a period
// that is not the one being shown.
//
// A week is always 7 days, so a day-count shift already lands exactly on
// the next/previous week's Monday-Sunday bounds. A calendar month is not a
// fixed length (28-31 days), so shifting a monthly window by its own day
// count drifts by up to a day at every month-length change and the drift
// compounds on every further step (e.g. August's 31 days pushed onto
// September would land on October 1st instead of September 30th). Monthly
// presets — and a custom range that is itself a full calendar month, so a
// second consecutive monthly step doesn't reintroduce the drift the first
// one avoided — are therefore shifted by calendar month and re-snapped to
// that month's own start/end rather than by day count. `last_30_days` and
// an arbitrary custom range have no calendar unit of their own, so they
// keep the day-count shift.
export const stepPeriod = (preset, custom, direction, now = dayjs()) => {
  const current = resolvePeriod(preset, custom, now);
  if (!current.from || !current.to) return { preset, custom };

  const isMonthly =
    preset === 'this_month' ||
    preset === 'last_month' ||
    (preset === 'custom' && isFullCalendarMonth(current.from, current.to));
  if (isMonthly) {
    const from = current.from.add(direction, 'month').startOf('month');
    return { preset: 'custom', custom: { from, to: from.endOf('month') } };
  }

  const days = current.to.startOf('day').diff(current.from.startOf('day'), 'day') + 1;
  const shift = days * direction;
  return {
    preset: 'custom',
    custom: { from: current.from.add(shift, 'day'), to: current.to.add(shift, 'day') },
  };
};

const PERIOD_PARAM = 'period';
const FROM_PARAM = 'from';
const TO_PARAM = 'to';
const DEFAULT_PERIOD = { preset: DEFAULT_PRESET, custom: { from: null, to: null } };

// The period lives in the URL so it survives a refresh, a shared link, and —
// the reason it is here rather than in one page's state — a move between
// Overview and Order Reports.
export const readPeriodFromParams = (searchParams) => {
  const preset = searchParams?.get(PERIOD_PARAM);
  if (!preset || !PERIOD_PRESETS.some((p) => p.value === preset)) return DEFAULT_PERIOD;
  if (preset !== 'custom') return { preset, custom: { from: null, to: null } };

  const from = dayjs(searchParams.get(FROM_PARAM));
  const to = dayjs(searchParams.get(TO_PARAM));
  // A custom period with no usable range is not a period. Falling back beats
  // rendering an empty statement the restaurant cannot explain.
  if (!from.isValid() || !to.isValid()) return DEFAULT_PERIOD;
  return { preset: 'custom', custom: { from, to } };
};

export const writePeriodToParams = (searchParams, { preset, custom } = DEFAULT_PERIOD) => {
  const next = new URLSearchParams(searchParams);
  next.set(PERIOD_PARAM, preset);
  if (preset === 'custom' && custom?.from && custom?.to) {
    next.set(FROM_PARAM, custom.from.format('YYYY-MM-DD'));
    next.set(TO_PARAM, custom.to.format('YYYY-MM-DD'));
  } else {
    next.delete(FROM_PARAM);
    next.delete(TO_PARAM);
  }
  return next;
};
